import { Link } from 'react-router';
import { type CSSProperties, type ReactNode } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip } from 'recharts';
import { ArrowDown, ArrowRight, ArrowUp, Minus } from 'lucide-react';

export type KpiHeroTone = 'neutral' | 'success' | 'warn' | 'danger' | 'info';

interface SparklinePoint {
  label: string;
  value: number;
}

export interface KpiHeroCardProps {
  label: string;
  value: string;
  /** Optional explanatory unit suffix (e.g. "원", "건") rendered alongside `value`. */
  unit?: string;
  /** Signed percentage change vs comparison period, e.g. 12.4 or -3.1. */
  deltaPercent?: number | null | undefined;
  /** Label rendered after the delta (e.g. "전일 대비", "지난 주 대비"). */
  deltaLabel?: string;
  /** Mini area chart series. Trend direction is derived from the slope. */
  sparkline?: ReadonlyArray<SparklinePoint>;
  /** Deep-link target. When omitted the card is rendered non-interactive. */
  to?: string;
  tone?: KpiHeroTone;
  hint?: string;
  testId: string;
  /** Optional icon rendered top-right. */
  icon?: ReactNode;
}

const TONE_ACCENT: Record<KpiHeroTone, string> = {
  neutral: 'var(--cta-primary)',
  success: 'var(--status-success-fg)',
  warn: 'var(--status-warn-fg)',
  danger: 'var(--status-danger-fg)',
  info: 'var(--status-info-fg)',
};

const formatDelta = (delta: number): string => {
  const abs = Math.abs(delta);
  const formatted = abs.toFixed(abs >= 10 ? 0 : 1);
  return `${delta > 0 ? '+' : delta < 0 ? '−' : ''}${formatted}%`;
};

const Delta = ({ deltaPercent, deltaLabel }: { deltaPercent: number; deltaLabel?: string }) => {
  const direction = deltaPercent > 0 ? 'up' : deltaPercent < 0 ? 'down' : 'flat';
  const Icon = direction === 'up' ? ArrowUp : direction === 'down' ? ArrowDown : Minus;
  const color =
    direction === 'up'
      ? 'var(--status-success-fg)'
      : direction === 'down'
        ? 'var(--status-danger-fg)'
        : 'var(--content-muted)';
  const bg =
    direction === 'up'
      ? 'var(--status-success-bg)'
      : direction === 'down'
        ? 'var(--status-danger-bg)'
        : 'var(--surface-1)';
  return (
    <div className="flex items-center gap-2">
      <span
        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums"
        style={{ background: bg, color }}
        aria-label={`변화율 ${formatDelta(deltaPercent)}`}
      >
        <Icon className="h-3 w-3" strokeWidth={2.5} />
        {formatDelta(deltaPercent)}
      </span>
      {deltaLabel ? (
        <span className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
          {deltaLabel}
        </span>
      ) : null}
    </div>
  );
};

const Sparkline = ({
  data,
  tone,
}: {
  data: ReadonlyArray<SparklinePoint>;
  tone: KpiHeroTone;
}) => {
  const accent = TONE_ACCENT[tone];
  const gradientId = `spark-${tone}`;
  return (
    <div className="h-12" aria-hidden="true">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data as SparklinePoint[]} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={accent} stopOpacity={0.18} />
              <stop offset="100%" stopColor={accent} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Tooltip
            cursor={{ stroke: accent, strokeWidth: 1, strokeDasharray: '3 3' }}
            contentStyle={{
              background: 'var(--surface-raised)',
              border: '1px solid var(--divider-strong)',
              borderRadius: 'var(--radius-card)',
              padding: '4px 8px',
              fontSize: 11,
            }}
            labelStyle={{ display: 'none' }}
            formatter={(value) => [new Intl.NumberFormat('ko-KR').format(Number(value)), '']}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={accent}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            isAnimationActive={true}
            animationDuration={600}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

interface CardBodyProps {
  label: string;
  value: string;
  unit?: string;
  deltaPercent?: number | null | undefined;
  deltaLabel?: string;
  sparkline?: ReadonlyArray<SparklinePoint>;
  tone: KpiHeroTone;
  hint?: string;
  icon?: ReactNode;
  interactive: boolean;
}

const CardBody = ({
  label,
  value,
  unit,
  deltaPercent,
  deltaLabel,
  sparkline,
  tone,
  hint,
  icon,
  interactive,
}: CardBodyProps) => {
  const cardStyle: CSSProperties = {
    background: 'var(--surface-raised)',
    border: '1px solid var(--divider-strong)',
    boxShadow: 'var(--shadow-card-rest)',
    transition: 'all var(--motion-normal) var(--motion-easing-out)',
  };
  return (
    <article
      className="group relative flex h-full flex-col gap-3 overflow-hidden rounded-[var(--radius-card)] p-5 hover:-translate-y-[2px] hover:shadow-[var(--shadow-card-hover)]"
      style={cardStyle}
      data-tone={tone}
      data-interactive={interactive ? 'true' : 'false'}
    >
      <header className="flex items-start justify-between gap-2">
        <span
          className="text-[11px] font-semibold uppercase tracking-[0.08em]"
          style={{ color: 'var(--content-muted)' }}
        >
          {label}
        </span>
        {icon ? (
          <span
            aria-hidden="true"
            className="flex h-7 w-7 items-center justify-center rounded-lg"
            style={{
              background: 'var(--surface-1)',
              color: TONE_ACCENT[tone],
            }}
          >
            {icon}
          </span>
        ) : null}
      </header>

      <div className="flex items-baseline gap-1">
        <strong
          className="text-[28px] font-semibold leading-none tracking-tight tabular-nums"
          style={{ color: 'var(--content-strong)' }}
          data-testid="dashboard-kpi-hero-value"
        >
          {value}
        </strong>
        {unit ? (
          <span
            className="text-[13px] font-medium"
            style={{ color: 'var(--content-muted)' }}
          >
            {unit}
          </span>
        ) : null}
      </div>

      {typeof deltaPercent === 'number' ? (
        <Delta deltaPercent={deltaPercent} {...(deltaLabel ? { deltaLabel } : {})} />
      ) : hint ? (
        <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
          {hint}
        </span>
      ) : null}

      {sparkline && sparkline.length > 1 ? <Sparkline data={sparkline} tone={tone} /> : null}

      {interactive ? (
        <ArrowRight
          aria-hidden="true"
          className="absolute bottom-4 right-4 h-4 w-4 opacity-0 transition-all group-hover:opacity-100 group-hover:translate-x-1"
          style={{ color: 'var(--content-muted)' }}
        />
      ) : null}
    </article>
  );
};

export const KpiHeroCard = ({
  label,
  value,
  unit,
  deltaPercent,
  deltaLabel,
  sparkline,
  to,
  tone = 'neutral',
  hint,
  testId,
  icon,
}: KpiHeroCardProps) => {
  const interactive = Boolean(to);
  const body = (
    <CardBody
      label={label}
      value={value}
      {...(unit ? { unit } : {})}
      {...(typeof deltaPercent === 'number' ? { deltaPercent } : {})}
      {...(deltaLabel ? { deltaLabel } : {})}
      {...(sparkline ? { sparkline } : {})}
      tone={tone}
      {...(hint ? { hint } : {})}
      {...(icon ? { icon } : {})}
      interactive={interactive}
    />
  );
  if (!interactive) {
    return <div data-testid={testId}>{body}</div>;
  }
  return (
    <Link
      to={to as string}
      data-testid={testId}
      className="block rounded-[var(--radius-card)] outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
    >
      {body}
    </Link>
  );
};
